/**
 * Demo 场景注册表
 * 每个场景包含: id, 显示标题, 图标, 数据文件
 * NavPill 的 Demo 选择器从此读取列表
 */
import type { AgentDemoStep } from "@/lib/agent-demo-data"
import { DEMO_STEPS as DEFAULT_STEPS } from "@/lib/agent-demo-data"

export interface DemoScenario {
  id: string
  title: string
  emoji: string
  description: string
  steps: AgentDemoStep[]
}

// Lazy imports for code splitting
const scenarioModules: Record<string, () => Promise<{ STEPS: AgentDemoStep[] }>> = {
  "default": async () => ({ STEPS: DEFAULT_STEPS }),
  // ── 场景 01: 自媒体运营 ──
  "media-a": () => import("./scenario-01-media"),
  "media-b": () => import("./scenario-01b-media-hotspot"),
  // ── 场景 02: 飞书协同 ──
  "feishu-a": () => import("./scenario-02-feishu"),
  "feishu-b": () => import("./scenario-02b-feishu-pdf"),
  // ── 场景 03: 电商运营 ──
  "ecommerce-a": () => import("./scenario-03-ecommerce"),
  "ecommerce-b": () => import("./scenario-03b-ecommerce-cs"),
  // ── 场景 06: 合规秘书 ──
  "legal-a": () => import("./scenario-06-legal"),
  "legal-b": () => import("./scenario-06b-legal-onboard"),
  // ── 场景 09: 炒股助手 ──
  "stock-a": () => import("./scenario-09-stock"),
  "stock-b": () => import("./scenario-09b-stock-sp500"),
  "stock-c": () => import("./scenario-09c-stock-trade"),
  "stock-d": () => import("./scenario-09d-stock-review"),
}

export const DEMO_SCENARIO_LIST: Omit<DemoScenario, "steps">[] = [
  { id: "default", title: "发布会方案", emoji: "🎯", description: "准备 AI 产品发布会方案 + PPT + 飞书发送" },
  // ── 01 自媒体 ──
  { id: "media-a", title: "自媒体·视频全流程", emoji: "🎬", description: "选题 → 脚本 → 配音 → 封面 → 多平台发布" },
  { id: "media-b", title: "自媒体·追热点写文", emoji: "🔥", description: "热点抓取 → 深度采集 → 公众号文章 → 配图" },
  // ── 02 飞书协同 ──
  { id: "feishu-a", title: "飞书·会议转材料", emoji: "📋", description: "录音 → 纪要 · 待办 · PPT · 飞书分发" },
  { id: "feishu-b", title: "飞书·PDF→任务", emoji: "📄", description: "PDF 需求文档 → 飞书文档 + Linear 任务" },
  // ── 03 电商 ──
  { id: "ecommerce-a", title: "电商·选品上架", emoji: "🛒", description: "选品 → 文案 → 主图 → 上架 → SEO 全链路" },
  { id: "ecommerce-b", title: "电商·客服投流", emoji: "🤖", description: "AI 客服自动接单 + 投流数据分析与优化" },
  // ── 06 合规 ──
  { id: "legal-a", title: "合规·合同扫描", emoji: "⚖️", description: "12 份合同批量风险扫描 · 本地 AI 处理" },
  { id: "legal-b", title: "合规·入职核查", emoji: "📋", description: "入职材料生成 + PIPL 合规检查 + 材料分发" },
  // ── 09 炒股 ──
  { id: "stock-a", title: "炒股·个股分析", emoji: "📈", description: "自选股行情 → 新闻情绪 → 技术面 → 基本面" },
  { id: "stock-b", title: "炒股·超卖扫描", emoji: "📉", description: "S&P 500 超卖筛选 → 基本面过滤 → 策略报告" },
  { id: "stock-c", title: "炒股·一键下单", emoji: "⚡", description: "自然语言下单 · Xerolite 本地执行" },
  { id: "stock-d", title: "炒股·复盘分析", emoji: "🔄", description: "盈亏复盘 → 行为分析 → 情绪化识别 → 改进" },
]

export async function loadScenarioSteps(id: string): Promise<AgentDemoStep[]> {
  const loader = scenarioModules[id] ?? scenarioModules["default"]
  const mod = await loader()
  return mod.STEPS
}

/** localStorage key for remembering last selected demo */
export const DEMO_STORAGE_KEY = "safeclaw-demo-scenario"
